import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Column, Row, Text, Icon, Card } from "@amzn/meridian";
import arrowLeftTokens from "@amzn/meridian-tokens/base/icon/arrow-left-large";
import InspectionService, {
    InspectionData,
    InspectionResult,
} from "src/services/inspectionService";
import CheckpointList from "./CheckpointList";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { getScheduledWorkflow, hydrateFromDraft } from "src/store/workflowSlice";
import {
    resetInspectionCaptureState,
    setScannedBarcode,
} from "src/store/inspectionCaptureSlice";
import { formatDate } from "src/utils/dateUtil";
import { computeDraftProgress } from "../utils/draftProgress";
import { computeResumePosition, isCageWalkResume } from "../utils/resumePosition";
import { orderGroupedResultsByQuestion } from "../utils/resultGrouping";
import { ResumeConfirmationState } from "./ResumeConfirmationPage";
import "./ResumeDraftPage.scss";
import ThemedButton from "./ThemedButton";


const ResumeDraftPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { activeWorkflow, questions, scheduledWorkflowLoading, scheduledWorkflowError } =
        useAppSelector((state) => state.workflow);

    const [inspectionData, setInspectionData] = useState<InspectionData | null>(null);
    const [presignedUrlMap, setPresignedUrlMap] = useState<Record<string, string>>({});
    const [groupedResults, setGroupedResults] = useState<Record<string, InspectionResult[]>>({});
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        const fetchDraft = async () => {
            try {
                const data = await InspectionService.fetchInspectionWithPresignedUrls(
                    inspectionId!
                );
                setInspectionData(data.inspectionData);
                setPresignedUrlMap(data.presignedUrlMap);
                setGroupedResults(data.groupedResults);
            } catch (err) {
                console.error("Failed to fetch draft inspection data:", err);
                setHasError(true);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDraft();
    }, [inspectionId]);

    // Load the workflow (question text + progress source) when it isn't already
    // in the store — e.g. a deep link/refresh straight onto this page.
    useEffect(() => {
        const austinInspectionId = inspectionData?.austinInspectionId;
        if (austinInspectionId && !activeWorkflow) {
            dispatch(getScheduledWorkflow(austinInspectionId));
        }
    }, [inspectionData, activeWorkflow, dispatch]);


    const handleBack = () => {
        navigate(-1);
    };

    const handleResume = () => {
        if (!activeWorkflow || !inspectionData) return;

        const resumePosition = computeResumePosition(activeWorkflow, inspectionData.results);

        if (resumePosition.complete) {
            // A fully-answered draft goes through the blur-review step first (same as
            // workflow completion in WorkflowExecutor). BlurReviewPage redirects straight
            // to /review when no image is blurry, so this adds the retake step only when
            // needed.
            navigate(`/configurationDrivenInspections/${inspectionId}/blurReview`);
            return;
        }

        if (isCageWalkResume(activeWorkflow, resumePosition)) {
            navigate(
                `/configurationDrivenInspections/${inspectionId}/resumeConfirm`,
                {
                    state: { inspectionId: inspectionId!, resumePosition } as ResumeConfirmationState,
                }
            );
            return;
        }

        dispatch(resetInspectionCaptureState());
        if (resumePosition.scannedBarcode) {
            dispatch(setScannedBarcode(resumePosition.scannedBarcode));
        }
        dispatch(
            hydrateFromDraft({
                inspectionId: inspectionId!,
                categoryIndex: resumePosition.categoryIndex,
                stepIndex: resumePosition.stepIndex,
                iterateStepIndex: resumePosition.iterateStepIndex,
                completedContainerCount: resumePosition.completedContainerCount,
            })
        );
        navigate("/configurationDrivenInspections/execute");
    };

    // Still loading if the draft is loading, or the workflow is in flight, or we
    // have a draft with a linked scheduled inspection but its workflow hasn't
    // arrived yet (deep-link case before/while the mount effect fetches it).
    const isWorkflowPending =
        scheduledWorkflowLoading ||
        (!!inspectionData?.austinInspectionId && !activeWorkflow && !scheduledWorkflowError);

    if (isLoading || isWorkflowPending) {
        return (
            <Column
                height="100vh"
                alignmentVertical="center"
                alignmentHorizontal="center"
            >
                <Text type="h200">Loading inspection data...</Text>
            </Column>
        );
    }

    if (
        hasError ||
        !inspectionData ||
        scheduledWorkflowError ||
        !inspectionData.austinInspectionId
    ) {
        return (
            <Column
                height="100vh"
                alignmentVertical="center"
                alignmentHorizontal="center"
            >
                <Text type="h200">Unable to load inspection data.</Text>
            </Column>
        );
    }

    const { datePart, timePart } = formatDate(inspectionData.createdAt);

    const progress = activeWorkflow
        ? computeDraftProgress(activeWorkflow, questions, inspectionData.results)
        : { percentComplete: 0, containersInspected: 0 };

    const questionEntries = orderGroupedResultsByQuestion(groupedResults, questions);

    return (
        <div
            className="resume-draft-page pwa-scrollable"
            data-inspection-id={inspectionId}
        >
            <Column spacingInset="500" spacing="400">
                <Row alignmentVertical="center" spacing="300">
                    <div
                        className="resume-draft-back-button"
                        onClick={handleBack}
                        role="button"
                    >
                        <Icon tokens={arrowLeftTokens} color="success" />
                    </div>
                    <Text type="b300" color="primary">
                        Inspection in progress
                    </Text>
                </Row>

                <Text type="h300" color="primary">
                    {inspectionData.templateName ?? "Inspection"}
                </Text>

                <Card spacingInset="none">
                    <Column spacingInset="400" spacing="300">
                        <Row spacing="200">
                            <Text type="b200" color="primary">
                                {datePart}
                            </Text>
                            <Text type="b200" color="secondary">
                                {timePart}
                            </Text>
                        </Row>
                        <Row widths={["fill", "fill"]} spacing="400">
                            <Column spacing="100">
                                <Text type="b100" color="primary">
                                    PROGRESS
                                </Text>
                                <Text type="h300" color="primary">
                                    {progress.percentComplete}%
                                </Text>
                            </Column>
                            <Column spacing="100">
                                <Text type="b100" color="primary">
                                    CONTAINERS INSPECTED
                                </Text>
                                <Text type="h300" color="primary">
                                    {progress.containersInspected}
                                </Text>
                            </Column>
                        </Row>
                    </Column>
                </Card>

                <Text type="h200" color="primary">
                    Check points
                </Text>

                <CheckpointList
                    questionEntries={questionEntries}
                    questions={questions}
                    presignedUrlMap={presignedUrlMap}
                />
            </Column>

            {/* Spacer for the fixed footer. */}
            <Row minHeight="80px" />

            <Row
                className="resume-draft-page__footer"
                spacingInset="400"
                width="100%"
                widths="fill"
            >
                <ThemedButton onClick={handleResume}>
                    RESUME INSPECTION
                </ThemedButton>
            </Row>
        </div>
    );
};

export default ResumeDraftPage;
